import axios from 'axios'
import { isEqual } from 'lodash'

export const CheckoutFragment = `
  fragment CheckoutFragment on Checkout {
    id
    webUrl
    totalTax
    subtotalPrice
    totalPrice
    lineItemsSubtotalPrice {
      amount
      currencyCode
    }
    customAttributes {
      key
      value
    }
    shippingDiscountAllocations {
      discountApplication {
        targetType
        allocationMethod
        targetSelection
        value
      }
    }
    discountApplications(first: 10) {
      edges {
        node {
          ... on DiscountCodeApplication {
            code
          }
          ... on AutomaticDiscountApplication {
            title
          }
          ... on ManualDiscountApplication {
            title
          }
          value {
            ... on MoneyV2 {
              amount
              currencyCode
            }
            ... on PricingPercentageValue {
              percentage
            }
          }
          targetType
        }
      }
    }
    lineItems(first: 250) {
      edges {
        node {
          id
          title
          variant {
            availableForSale
            id
            sku
            title
            product {
              id
              handle
              collections(first: 5) {
                edges {
                  node {
                    id
                    handle
                  }
                }
              }
            }
            image {
              src
            }
            price
            selectedOptions {
              name
              value
            }
          }
          quantity
          customAttributes {
            key
            value
          }
          discountAllocations {
            allocatedAmount {
              amount
              currencyCode
            }
            discountApplication {
              allocationMethod
              targetSelection
              targetType
              value
            }
          }
        }
      }
    }
  }
`

export const getCheckout = async ({ checkoutId }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        query getCheckout($id: ID!) {
          node(id: $id) {
            ... on Checkout {
              ...CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        id: checkoutId
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data.data.node
}

export const createCheckoutMutation = async ({ input }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        mutation checkoutCreate($input: CheckoutCreateInput!) {
          checkoutCreate(input: $input) {
            userErrors {
              field
              message
            }
            checkout {
              ...CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        input
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data.data.checkoutCreate.checkout
}

const checkoutLineItemsReplaceMutation = async ({ checkoutId, lineItems }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        mutation checkoutLineItemsReplace($lineItems: [CheckoutLineItemInput!]!, $checkoutId: ID!) {
          checkoutLineItemsReplace(lineItems: $lineItems, checkoutId: $checkoutId) {
            userErrors {
              field
              message
            }
            checkout {
              ... CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        checkoutId: checkoutId,
        lineItems: lineItems
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data.data.checkoutLineItemsReplace?.checkout
}

const existingCheckoutLineItems = ({ checkout }) => {
  return checkout.lineItems.edges.map(lineItem => ({
    quantity: lineItem.node.quantity,
    variantId: lineItem.node.variant.id,
    customAttributes: lineItem.node.customAttributes
  }))
}

export const checkoutLineItemsAdd = async ({ checkout, lineItems }) => {
  const finalLineItems = [...existingCheckoutLineItems({ checkout })]

  const formattedLineItems = lineItems.map(lineItem => {
    let variantId = lineItem.variantId

    if (variantId.includes('ProductVariant')) {
      const splitVariantId = variantId.split('__')
      variantId = splitVariantId[splitVariantId.length - 1]
    }

    return {
      ...lineItem,
      variantId,
      customAttributes: lineItem.customAttributes
    }
  })

  formattedLineItems.forEach(l => {
    const existsIndex = finalLineItems.findIndex(
      existingLineItem =>
        existingLineItem.variantId === l.variantId && isEqual(existingLineItem.customAttributes, l.customAttributes)
    )

    if (existsIndex >= 0 && isEqual(finalLineItems[existsIndex].customAttributes, l.customAttributes)) {
      finalLineItems[existsIndex] = {
        ...finalLineItems[existsIndex],
        quantity: finalLineItems[existsIndex].quantity + l.quantity
      }
    } else {
      finalLineItems.push(l)
    }
  })

  const response = await checkoutLineItemsReplaceMutation({
    checkoutId: checkout.id,
    lineItems: finalLineItems
  })

  return response
}

export const checkoutLineItemsUpdate = async ({ checkout, lineItems }) => {
  let existingLineItems = existingCheckoutLineItems({ checkout })

  lineItems.forEach(lineItem => {
    const index = existingLineItems.findIndex(l => {
      const idMatch = l.variantId === lineItem.variantId
      const existingOrderType = l.customAttributes?.find(attr => attr.key === 'orderType')?.value
      const orderType = lineItem.customAttributes?.find(attr => attr.key === 'orderType')?.value
      const orderTypeMatch = existingOrderType === orderType
      return idMatch && orderTypeMatch
    })

    if (index > -1) {
      existingLineItems[index].quantity = lineItem.quantity
    } else {
      existingLineItems = [
        ...existingLineItems,
        {
          quantity: lineItem.quantity,
          variantId: lineItem.variantId,
          customAttributes: lineItem.customAttributes
        }
      ]
    }
  })

  const response = checkoutLineItemsReplaceMutation({
    checkoutId: checkout.id,
    lineItems: existingLineItems
  })

  return response
}

export const checkoutLineItemsRemove = async ({ checkout, itemsToRemove }) => {
  const existingLineItems = existingCheckoutLineItems({ checkout })

  for (let item of itemsToRemove) {
    const { variantId, customAttributes } = item
    const index = existingLineItems.findIndex(
      l => l.variantId === variantId && isEqual(l.customAttributes, customAttributes)
    )
    existingLineItems.splice(index, 1)
  }

  const response = checkoutLineItemsReplaceMutation({
    checkoutId: checkout.id,
    lineItems: existingLineItems
  })

  return response
}

export const checkoutDiscountCodeApply = async ({ checkout, discountCode }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        mutation checkoutDiscountCodeApplyV2($discountCode: String!, $checkoutId: ID!) {
          checkoutDiscountCodeApplyV2(discountCode: $discountCode, checkoutId: $checkoutId) {
            userErrors {
              field
              message
            }
            checkout {
              ...CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        checkoutId: checkout.id,
        discountCode
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  if (response.data.data.checkoutDiscountCodeApplyV2.userErrors.length > 0) {
    return {
      error: 'Unable to find a valid discount matching the code entered'
    }
  }

  return response.data.data.checkoutDiscountCodeApplyV2.checkout
}

export const checkoutDiscountCodeRemove = async ({ checkout }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        mutation checkoutDiscountCodeRemove($checkoutId: ID!) {
          checkoutDiscountCodeRemove(checkoutId: $checkoutId) {
            userErrors {
              field
              message
            }
            checkout {
              ...CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        checkoutId: checkout.id
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data.data.checkoutDiscountCodeRemove.checkout
}

export const checkoutUpdateCustomAttribute = async ({ checkoutId, input }) => {
  const response = await axios.post(
    process.env.GATSBY_API_URI,
    {
      query: `
        mutation checkoutAttributesUpdateV2($checkoutId: ID!, $input: CheckoutAttributesUpdateV2Input!) {
          checkoutAttributesUpdateV2(checkoutId: $checkoutId, input: $input) {
            userErrors {
              message
              field
            }
            checkout {
              ...CheckoutFragment
            }
          }
        }
        ${CheckoutFragment}
      `,
      variables: {
        checkoutId,
        input
      }
    },
    {
      headers: {
        'X-Shopify-Storefront-Access-Token': process.env.GATSBY_API_ACCESS_TOKEN,
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data.data.checkoutAttributesUpdateV2.checkout
}
